import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ticket } from "lucide-react";
import { adminEvents, adminCreateEvent, adminUpdateEvent, adminDeleteEvent, apiError } from "../../lib/api";
import { Field, Input, Textarea, Select, Btn, Modal, ImageUpload } from "./AdminKit";

const blank = { title: "", date: "", time: "", venue: "", description: "", image: "",
  ticket_type: "none", ticket_url: "", ticket_price: 0, ticket_capacity: 0, published: true };

export default function AdminEvents() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => adminEvents().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setForm({ ...blank, ...ev }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, ticket_price: parseFloat(form.ticket_price) || 0, ticket_capacity: parseInt(form.ticket_capacity) || 0 };
    try {
      if (editing) await adminUpdateEvent(editing.id, payload);
      else await adminCreateEvent(payload);
      toast.success(editing ? "Event updated." : "Event created.");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try { await adminDeleteEvent(ev.id); toast.success("Event deleted."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl text-nead-navy">Events</h1>
          <p className="mt-1 text-sm text-nead-navy/55">Add festivals and gigs shown on the public events calendar.</p>
        </div>
        <Btn onClick={openNew} data-testid="event-add-btn"><Plus className="h-4 w-4" /> Add event</Btn>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-black/[0.07] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-nead-mist text-nead-navy/55 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3 font-semibold">Event</th><th className="text-left px-5 py-3 font-semibold">Date</th><th className="text-left px-5 py-3 font-semibold">Tickets</th><th className="text-left px-5 py-3 font-semibold">Status</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {list.map((ev) => (
              <tr key={ev.id} data-testid={`event-row-${ev.id}`}>
                <td className="px-5 py-4 font-semibold text-nead-navy">{ev.title}<div className="text-xs font-normal text-nead-navy/50">{ev.venue}</div></td>
                <td className="px-5 py-4 text-nead-navy/70">{ev.date} {ev.time}</td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center gap-1.5 text-nead-navy/70 capitalize"><Ticket className="h-3.5 w-3.5" />{ev.ticket_type}{ev.ticket_type === "self" ? ` · ${ev.tickets_sold || 0}/${ev.ticket_capacity || "∞"}` : ""}</span>
                </td>
                <td className="px-5 py-4">{ev.published ? <span className="text-emerald-600 font-medium">Published</span> : <span className="text-nead-navy/40">Draft</span>}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`} className="p-2 text-nead-navy/50 hover:text-nead-pink"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(ev)} data-testid={`event-delete-${ev.id}`} className="p-2 text-nead-navy/50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-nead-navy/40">No events yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit event" : "Add event"} wide>
        <form onSubmit={save} className="space-y-4" data-testid="event-form">
          <Field label="Title"><Input required value={form.title} onChange={set("title")} data-testid="event-title" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date"><Input type="date" required value={form.date} onChange={set("date")} data-testid="event-date" /></Field>
            <Field label="Time"><Input type="time" value={form.time} onChange={set("time")} data-testid="event-time" /></Field>
          </div>
          <Field label="Venue"><Input value={form.venue} onChange={set("venue")} data-testid="event-venue" /></Field>
          <Field label="Image"><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testId="event-image" /></Field>
          <Field label="Description"><Textarea rows={3} value={form.description} onChange={set("description")} /></Field>
          <Field label="Ticketing">
            <Select value={form.ticket_type} onChange={set("ticket_type")} data-testid="event-ticket-type">
              <option value="none">No tickets</option>
              <option value="external">External link</option>
              <option value="self">Self-hosted (sell on site)</option>
            </Select>
          </Field>
          {form.ticket_type === "external" && (
            <Field label="Ticket URL"><Input value={form.ticket_url} onChange={set("ticket_url")} placeholder="https://skiddle.com/…" data-testid="event-ticket-url" /></Field>
          )}
          {form.ticket_type === "self" && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price (£)"><Input type="number" step="0.01" min="0" value={form.ticket_price} onChange={set("ticket_price")} data-testid="event-ticket-price" /></Field>
              <Field label="Capacity"><Input type="number" min="0" value={form.ticket_capacity} onChange={set("ticket_capacity")} data-testid="event-ticket-capacity" /></Field>
            </div>
          )}
          <label className="flex items-center gap-2.5 text-sm text-nead-navy/70">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} data-testid="event-published" className="h-4 w-4 accent-[#e6007e]" />
            Published (visible on the website)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="event-save">{busy ? "Saving…" : "Save event"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
